import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { IDataProviderSort } from 'core-components/types'

import { MOUNT_WAITER_TIMEOUT, WIP_TIMEOUT } from '../constants/sourse'
import { ISideEffects, IUseSource } from '../models/IUseSource'

export const useSource = <P, R>({
  source,
  converter,
  sourceUpdateCount,
  filters,
  measure,
  onDataLoad,
  onDataLoadError,
}: IUseSource<P, R>) => {
  const [data, setData] = useState<P[]>([])
  const [total, setTotal] = useState<number>(0)
  const [wip, setWip] = useState(true)

  const sideEffectRef = useRef<ISideEffects>({ unmounted: false, mounted: false, wipTimeout: 0 })

  const openNode = useCallback(
    async (value: any) => {
      const localData = await source.openNodes([value])
      let result: any[] = []
      if (!sideEffectRef.current.unmounted && localData) {
        if (converter) {
          result = converter(localData.items)
        } else {
          result = localData.items as P[]
        }
        setData(result)
      }
      return result
    },
    [source]
  )

  const toggleWipTimer = useCallback((flag: boolean) => {
    if (flag) {
      if (!sideEffectRef.current.wipTimeout) {
        sideEffectRef.current.wipTimeout = window.setTimeout(() => {
          if (!sideEffectRef.current.unmounted) {
            setWip(true)
          }
        }, WIP_TIMEOUT)
      }
    } else {
      if (sideEffectRef.current.wipTimeout) {
        clearTimeout(sideEffectRef.current.wipTimeout)
        sideEffectRef.current.wipTimeout = 0
      }
      setWip(false)
    }
  }, [])

  const fetch = () => {
    if (sourceUpdateCount === -1) {
      return
    }

    toggleWipTimer(true)
    measure?.startLoadData()
    source
      .fetch()
      .then((localData) => {
        const loadData = () => {
          if (!sideEffectRef.current.mounted) {
            setTimeout(loadData, MOUNT_WAITER_TIMEOUT)
            return
          }
          if (!sideEffectRef.current.unmounted) {
            const newData = converter ? converter(localData.items) : localData.items
            if (localData.error) {
              onDataLoadError && onDataLoadError(new Error(localData.error))
            } else {
              const preparedItems = onDataLoad && onDataLoad(newData, localData.total)
              setData(preparedItems || newData)
              setTotal(localData.total)
            }
            toggleWipTimer(false)
          }
        }

        loadData()
      })
      .catch((error) => {
        if (onDataLoadError) {
          onDataLoadError(error)
        } else {
          console.error('Table::: Data load error', error)
        }
      })
      .finally(() => {
        measure?.stopLoadData()
        toggleWipTimer(false)
      })
  }

  const checkFilters = async () => {
    if (filters) {
      source.setFilter(filters)
    }
  }

  const checkSort = () => {
    const sort = source.combineSort()
    if (sort && !Array.isArray(sort)) {
      source.setSort([sort as IDataProviderSort])
    }
  }

  useMemo(() => {
    checkSort()
  }, [])

  useMemo(() => {
    checkFilters().then(() => fetch())
  }, [filters, sourceUpdateCount, source])

  useEffect(() => {
    return source.subscribe('change', () => {
      checkSort()
      fetch()
    })
  }, [source])

  useLayoutEffect(() => {
    sideEffectRef.current.mounted = true
    return () => {
      sideEffectRef.current.unmounted = true
    }
  }, [])

  return { wip, data, openNode, total }
}
